// src/utils/exportStoryboardPdf.js
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { translate } from "./i18n";
import { displayTagValue } from "./shotTags";
import { getShotDisplayNumber } from "./shotNumber";
import {
  getShotFrameFormat,
  getShotColorFormat,
  effectiveFrameFormatValue,
  effectiveColorFormatValue,
} from "./frameFormats";
import { getSceneNumberLabel } from "./parseScenes";

// laisse le navigateur repeindre (utile pour animer une barre de progression)
const yieldToUI = () => new Promise((r) => setTimeout(r, 0));

const A4 = { w: 595.28, h: 841.89 };

const L = {
  margin: { l: 36, r: 36, t: 36, b: 36 },
  headerH: 26,
  gapBlocks: 42,
  gapX: 12,
  pad: 8,

  desiredFrameW: 265,
  desiredFrameH: 149,

  // Pills
  pillFont: 9,
  pillPadX: 7,
  pillPadY: 3.5,
  pillGapX: 6,
  pillGapY: 6,
  pillFill: rgb(0.95, 0.95, 0.97),
  pillBorder: rgb(0.2, 0.2, 0.25),
  pillText: rgb(0.05, 0.05, 0.08),

  // Text/Image boxes (right column)
  boxBorder: rgb(0.2, 0.2, 0.25),
  boxFill: rgb(1, 1, 1),
  labelSize: 9.2,
  bodySize: 9.2,
  lineH: 12,
  boxPad: 7,
  boxTitleGap: 5,

  // Limits to avoid insane blocks
  maxDescH: 90,
  maxScriptH: 140,
  maxImageH: 140,

  // Notes (under frame, left column)
  notesGapFromFrame: 4,
  notesLineInsetX: 10,
  notesLineInsetTop: 16,
  notesLineGap: 14,
  notesBottomPad: 10,
  notesLineColor: rgb(0.75, 0.75, 0.8),

  // Block separator
  blockSepColor: rgb(0.75, 0.75, 0.8),
  blockSepThickness: 1.3,
};

function dateStamp() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return { y, m, d };
}

const pad2 = (n) => String(n).padStart(2, "0");
const safeInt = (n, fb = 0) => (Number.isFinite(Number(n)) ? Number(n) : fb);
const clampInt = (v, min, max, fb) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return fb;
  return Math.max(min, Math.min(max, Math.round(x)));
};

function inferImageInfo(shot) {
  const img = shot?.image;
  const dataUrl = typeof img === "string" ? img : img?.dataUrl || "";
  const type =
    typeof img === "object" && img?.type
      ? img.type
      : dataUrl.startsWith("data:")
      ? dataUrl.slice(5, dataUrl.indexOf(";"))
      : "";
  return { dataUrl, type };
}

function dataUrlToBytes(dataUrl) {
  const i = String(dataUrl || "").indexOf(",");
  const b64 = i >= 0 ? dataUrl.slice(i + 1) : dataUrl;
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let k = 0; k < bin.length; k++) bytes[k] = bin.charCodeAt(k);
  return bytes;
}

// ✅ Convertit n'importe quelle image décodable par le navigateur -> PNG via canvas.
// (utilisé en dernier recours pour les formats/encodages que pdf-lib refuse :
//  WebP, JPEG CMYK/progressif, etc.)
function anyImageToPngDataUrl(srcDataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 1;
          canvas.height = img.naturalHeight || img.height || 1;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = srcDataUrl;
    } catch {
      resolve(null);
    }
  });
}

async function embedImageIfSupported(pdfDoc, dataUrl, mimeHint = "") {
  const mime = String(mimeHint || "").toLowerCase();
  const src = String(dataUrl || "");

  // 1) Tentative directe selon le type déclaré
  try {
    if (mime.includes("png") || src.startsWith("data:image/png")) {
      return await pdfDoc.embedPng(dataUrlToBytes(src));
    }
    if (
      mime.includes("jpeg") ||
      mime.includes("jpg") ||
      src.startsWith("data:image/jpeg") ||
      src.startsWith("data:image/jpg")
    ) {
      return await pdfDoc.embedJpg(dataUrlToBytes(src));
    }
  } catch {
    // le format déclaré n'est pas embarquable tel quel -> on tombera sur le fallback canvas
  }

  // 2) Fallback universel : re-encodage en PNG via canvas (WebP, CMYK, formats exotiques…)
  try {
    const pngDataUrl = await anyImageToPngDataUrl(src);
    if (pngDataUrl) {
      return await pdfDoc.embedPng(dataUrlToBytes(pngDataUrl));
    }
  } catch {
    // ignore
  }

  return null;
}

function parseSceneHeadingParts(sceneTitle) {
  const t0 = (sceneTitle || "").trim();
  if (!t0) return { intExt: "", location: "", timeOfDay: "" };

  const t = t0
    .replace(/^SC\s*\d+\s*[.:\-]?\s*/i, "")
    .replace(/^(SCENE|SCÈNE)\s*\d+\s*[.:\-]?\s*/i, "")
    .trim();

  const m = t.match(
    /^(INT\.?\/EXT\.?|INT\/EXT|I\/E|INT\.?|EXT\.?|INT|EXT)\b\.?\s*(.*)$/i
  );

  const intExt = (m?.[1] || "").replace(/\./g, "").toUpperCase();
  const rest = (m?.[2] || t).trim();

  const parts = rest.split(/\s*-\s*/).filter(Boolean);
  if (parts.length <= 1) return { intExt, location: rest, timeOfDay: "" };

  const timeOfDay = parts[parts.length - 1].trim();
  const location = parts.slice(0, -1).join(" - ").trim();
  return { intExt, location, timeOfDay };
}

function wrapTextToLines(text, font, size, maxW, maxLines = Infinity) {
  const raw = safeWinAnsiText(String(text ?? "")).replace(/\r/g, "");
  if (!raw.trim()) return [];

  const out = [];
  const paras = raw.split("\n");

  const pushLine = (line) => {
    const words = line.split(/\s+/).filter(Boolean);
    if (!words.length) {
      out.push("");
      return;
    }
    let cur = words[0];
    for (let i = 1; i < words.length; i++) {
      const test = cur + " " + words[i];
      if (font.widthOfTextAtSize(test, size) <= maxW) cur = test;
      else {
        out.push(cur);
        cur = words[i];
        if (out.length >= maxLines) return;
      }
    }
    out.push(cur);
  };

  for (const p of paras) {
    if (out.length >= maxLines) break;
    if (!p.trim()) {
      out.push("");
      continue;
    }
    pushLine(p);
    if (out.length >= maxLines) break;
  }

  if (out.length > maxLines) out.length = maxLines;
  if (out.length === maxLines && maxLines !== Infinity) {
    const last = out[out.length - 1] || "";
    out[out.length - 1] = last.endsWith("…") ? last : last ? last + "…" : "…";
  }
  return out;
}

function drawBox(page, x, y, w, h, bw = 1) {
  page.drawRectangle({
    x,
    y,
    width: w,
    height: h,
    borderColor: L.boxBorder,
    borderWidth: bw,
    color: L.boxFill,
  });
}

function pillSize(font, text) {
  const w = font.widthOfTextAtSize(text, L.pillFont) + L.pillPadX * 2;
  const h = L.pillFont + L.pillPadY * 2;
  return { w, h };
}

function drawPill(page, font, x, topY, text) {
  const { w, h } = pillSize(font, text);
  const y = topY - h;

  page.drawRectangle({
    x,
    y,
    width: w,
    height: h,
    borderColor: L.pillBorder,
    borderWidth: 1,
    color: L.pillFill,
  });
  page.drawText(text, {
    x: x + L.pillPadX,
    y: y + L.pillPadY,
    size: L.pillFont,
    font,
    color: L.pillText,
  });
  return { w, h, bottomY: y };
}

function multiLinePillMetrics(font, text, maxW, maxLines = 3) {
  const safeText =
    typeof safeWinAnsiText === "function"
      ? safeWinAnsiText(text)
      : String(text ?? "");

  const t = safeText.trim() || "—";
  const innerW = Math.max(12, maxW - L.pillPadX * 2);

  const lines = wrapTextToLines(t, font, L.pillFont, innerW, maxLines);
  const finalLines = lines.length ? lines : ["—"];

  const lineGap = 2.5;
  const textW = Math.max(
    ...finalLines.map((ln) => font.widthOfTextAtSize(ln, L.pillFont)),
    0
  );

  const w = Math.min(maxW, textW + L.pillPadX * 2);
  const h =
    finalLines.length * L.pillFont +
    (finalLines.length - 1) * lineGap +
    L.pillPadY * 2;

  return {
    text: t,
    lines: finalLines,
    w,
    h,
    lineGap,
  };
}

function drawMultiLinePill(page, font, x, topY, metrics) {
  const y = topY - metrics.h;

  page.drawRectangle({
    x,
    y,
    width: metrics.w,
    height: metrics.h,
    borderColor: L.pillBorder,
    borderWidth: 1,
    color: L.pillFill,
  });

  let yy = topY - L.pillPadY - L.pillFont;

  for (const line of metrics.lines) {
    page.drawText(line, {
      x: x + L.pillPadX,
      y: yy,
      size: L.pillFont,
      font,
      color: L.pillText,
    });

    yy -= L.pillFont + metrics.lineGap;
  }

  return {
    w: metrics.w,
    h: metrics.h,
    bottomY: y,
  };
}

function layoutHeaderPills(font, items, maxW) {
  const rows = [];
  let row = [];
  let rowW = 0;

  const slashW = font.widthOfTextAtSize("/", L.pillFont) + 16;

  for (const raw of items) {
    const item = multiLinePillMetrics(font, raw, maxW, 3);
    const neededW = row.length ? slashW + item.w : item.w;

    if (row.length && rowW + neededW > maxW) {
      rows.push(row);
      row = [item];
      rowW = item.w;
    } else {
      row.push(item);
      rowW += neededW;
    }
  }

  if (row.length) rows.push(row);

  const h = rows.reduce((acc, r, idx) => {
    const rowH = Math.max(...r.map((it) => it.h), 0);
    return acc + rowH + (idx ? L.pillGapY : 0);
  }, 0);

  return { rows, h };
}

function drawHeaderPills(page, font, x, topY, layout) {
  let yy = topY;
  let bottomY = topY;

  const slashW = font.widthOfTextAtSize("/", L.pillFont) + 16;

  for (const row of layout.rows || []) {
    const rowH = Math.max(...row.map((it) => it.h), 0);
    let xx = x;

    row.forEach((item, idx) => {
      if (idx > 0) {
        page.drawText("/", {
          x: xx + 6,
          y: yy - rowH / 2 - L.pillFont / 2 + 2,
          size: L.pillFont,
          font,
          color: L.pillText,
        });

        xx += slashW;
      }

      const out = drawMultiLinePill(page, font, xx, yy, item);
      xx += out.w;
      bottomY = Math.min(bottomY, out.bottomY);
    });

    yy -= rowH + L.pillGapY;
  }

  return { bottomY };
}

function layoutPills(font, items, maxW, maxLines = 3) {
  const rows = [];
  let row = [];
  let rowW = 0;

  const measure = (t) => pillSize(font, t).w;

  for (const it of items) {
    const w = measure(it);
    const need = row.length ? L.pillGapX + w : w;

    if (row.length && rowW + need > maxW) {
      rows.push(row);
      row = [it];
      rowW = w;
      if (rows.length >= maxLines) break;
    } else {
      row.push(it);
      rowW += need;
    }
  }
  if (rows.length < maxLines && row.length) rows.push(row);

  const totalUsed = rows.reduce((a, r) => a + r.length, 0);
  if (totalUsed < items.length && rows.length) {
    const ell = "…";
    const lastRow = rows[rows.length - 1];
    const curW = lastRow.reduce(
      (acc, t, idx) => acc + measure(t) + (idx ? L.pillGapX : 0),
      0
    );
    const ellW = measure(ell);
    if (curW + L.pillGapX + ellW <= maxW) lastRow.push(ell);
    else lastRow[lastRow.length - 1] = ell;
  }

  const h = pillSize(font, "X").h;
  const totalH = rows.length ? rows.length * h + (rows.length - 1) * L.pillGapY : 0;
  return { rows, pillH: h, h: totalH };
}

function drawPillRows(page, font, x, topY, rows) {
  let y = topY;
  let minBottom = topY;
  for (const row of rows) {
    let xx = x;
    for (const t of row) {
      const out = drawPill(page, font, xx, y, t);
      xx += out.w + L.pillGapX;
      minBottom = Math.min(minBottom, out.bottomY);
    }
    y -= pillSize(font, "X").h + L.pillGapY;
  }
  return { bottomY: minBottom };
}

function headingLine(r, fields) {
  const parts = [];
  if (fields.intExt && r.intExt) parts.push(r.intExt);
  if (fields.location && r.location) parts.push(r.location);
  if (fields.timeOfDay && r.timeOfDay) parts.push(r.timeOfDay);
  return parts.join(" - ");
}

function tagPills(r, fields, labels, lang) {
  const out = [];
  // ✅ MASTER en premier, avec les tags du plan
  if (r.isMaster) out.push("MASTER");
  if (fields.shotSize && r.shotSize) {
    out.push(`${labels.shotSize}: ${displayTagValue("shotSize", r.shotSize, lang)}`);
  }
  if (fields.angle && r.angle) {
    out.push(`${labels.angle}: ${displayTagValue("angle", r.angle, lang)}`);
  }
  if (fields.focus && r.focus) {
    out.push(`${labels.focus}: ${displayTagValue("focus", r.focus, lang)}`);
  }
  if (fields.framing && r.framing) {
    out.push(`${labels.framing}: ${displayTagValue("framing", r.framing, lang)}`);
  }
  if (fields.movement && r.movement) {
    out.push(`${labels.movement}: ${displayTagValue("movement", r.movement, lang)}`);
  }
  if (fields.focal && r.focal) {
    out.push(`${labels.focal}: ${String(r.focal)}`);
  }
  if (fields.lights && String(r.lights || "").trim()) {
  out.push(`${labels.lights}: ${String(r.lights).trim()}`);
  }
  if (fields.estimation && Number.isFinite(Number(r.estimation))) {
    out.push(`${labels.estimation}: ${String(r.estimation)}s`);
  }
  if (fields.ratio && r.ratioLabel) {
    out.push(`${labels.ratio}: ${r.ratioLabel}`);
  }
  if (fields.colorFormat && r.colorLabel) {
    out.push(`${labels.colorFormat}: ${r.colorLabel}`);
  }
  return out;
}

function calcTextBoxLinesAndH({ text, font, w, maxH, footerLines = 0 }) {
  const innerW = w - L.boxPad * 2;

  const baseMinH = L.boxPad + L.labelSize + L.boxTitleGap + L.boxPad;

  const availableForText = maxH - baseMinH - footerLines * L.lineH;
  const maxLines = Math.max(1, Math.floor(availableForText / L.lineH));

  const lines = wrapTextToLines(text, font, L.bodySize, innerW, maxLines);

  const hWanted = baseMinH + (lines.length + footerLines) * L.lineH;
  const h = Math.min(maxH, Math.max(baseMinH, hWanted));

  return { lines, h };
}

function ellipsizeToWidth(font, text, size, maxW) {
  const t = safeWinAnsiText(text);
  if (!t) return "";
  if (font.widthOfTextAtSize(t, size) <= maxW) return t;

  let s = t;
  while (s.length > 1 && font.widthOfTextAtSize(s + "...", size) > maxW) {
    s = s.slice(0, -1);
  }
  return s.length ? s + "..." : "...";
}


function safeWinAnsiText(str) {
  return String(str ?? "")
    .replaceAll("→", "->")
    .replaceAll("—", "-")
    .replaceAll("–", "-")
    .replaceAll("…", "...")
    .replace(/[“”]/g, '"')
    .replace(/[’]/g, "'");
}



function drawTextBox(page, {
  x, topY, w, title, lines, h,
  font, fontBold,
  bodyFont = font,

  // ✅ nouveau : texte à droite sur la ligne du titre
  headerRightText = "",
  headerRightFont = bodyFont,
  headerRightSize = 8.2,
  headerRightColor = rgb(0.25, 0.25, 0.28),
}) {
  drawBox(page, x, topY - h, w, h, 0.9);

  const titleY = topY - L.boxPad - L.labelSize;

  // Titre gauche
  page.drawText(title, {
    x: x + L.boxPad,
    y: titleY,
    size: L.labelSize,
    font: fontBold,
    color: rgb(0, 0, 0),
  });

  // ✅ Texte droite (repère) sur la même ligne
  if (headerRightText) {
    const maxRightW = w * 0.55; // limite pour éviter collision avec le titre
    const txt = ellipsizeToWidth(headerRightFont, headerRightText, headerRightSize, maxRightW);
    const tw = headerRightFont.widthOfTextAtSize(txt, headerRightSize);

    page.drawText(txt, {
      x: x + w - L.boxPad - tw,
      y: titleY + (L.labelSize - headerRightSize) * 0.35, // align visuel
      size: headerRightSize,
      font: headerRightFont,
      color: headerRightColor,
    });
  }

  // ----- le reste inchangé : rendu des lignes de body -----
  let yy = titleY - L.boxTitleGap - L.bodySize;
  for (const ln of lines) {
    page.drawText(ln, {
      x: x + L.boxPad,
      y: yy,
      size: L.bodySize,
      font: bodyFont,
      color: rgb(0, 0, 0),
    });
    yy -= L.lineH;
  }
}


function calcImageBoxAndH({ pdfImage, w, maxH }) {
  const innerW = w - L.boxPad * 2;
  const baseMinH = L.boxPad + L.labelSize + L.boxTitleGap + L.boxPad + L.boxPad;
  const maxInnerH = Math.max(10, maxH - baseMinH);

  const iw = pdfImage.width || 1;
  const ih = pdfImage.height || 1;

  let drawW = innerW;
  let drawH = (ih / iw) * drawW;

  if (drawH > maxInnerH) {
    drawH = maxInnerH;
    drawW = (iw / ih) * drawH;
  }

  const hWanted = baseMinH + drawH;
  const h = Math.min(maxH, Math.max(baseMinH, hWanted));

  return { h, drawW, drawH };
}

function drawImageBox(page, { x, topY, w, title, h, drawW, drawH, fontBold, pdfImage }) {
  drawBox(page, x, topY - h, w, h, 0.9);

  const titleY = topY - L.boxPad - L.labelSize;
  page.drawText(title, {
    x: x + L.boxPad,
    y: titleY,
    size: L.labelSize,
    font: fontBold,
    color: rgb(0, 0, 0),
  });

  const innerW = w - L.boxPad * 2;
  const imgX = x + L.boxPad + (innerW - drawW) / 2;

  const imgTopY = titleY - L.boxTitleGap - L.boxPad;
  const imgY = imgTopY - drawH;

  page.drawImage(pdfImage, {
    x: imgX,
    y: imgY,
    width: drawW,
    height: drawH,
  });
}

function notesAreaH(linesCount) {
  const n = Math.max(1, Math.min(6, Number(linesCount) || 2));
  return L.notesLineInsetTop + 16 + (n - 1) * L.notesLineGap + L.notesBottomPad;
}

function drawNotesArea(page, { x, topY, w, linesCount }) {
  const n = Math.max(1, Math.min(6, Number(linesCount) || 2));
  const firstY = topY - L.notesLineInsetTop - 16;

  for (let i = 0; i < n; i++) {
    const yy = firstY - i * L.notesLineGap;
    page.drawLine({
      start: { x: x + L.notesLineInsetX, y: yy },
      end: { x: x + w - L.notesLineInsetX, y: yy },
      thickness: 0.7,
      color: L.notesLineColor,
    });
  }
}

function computeBlock(font, fontBold, courier, r, fields, layoutCfg, contentW, contentH, labels, lang, boxTitles) {
  const scales = [];
  for (let s = 1.0; s >= 0.65; s -= 0.03) scales.push(Number(s.toFixed(2)));

  const targetH = contentH * 0.52;
  let best = null;

  const wantNotes = !!fields.notes;
  const notesLines = clampInt(layoutCfg.notesLines, 1, 6, 2);

  const wantImage = !!fields.image;
  const imagePlacement = layoutCfg.imagePlacement === "inFrame" ? "inFrame" : "underScript";
  const hasPdfImage = !!r._pdfImage;

  for (const s of scales) {
    const frameRatio = Number(r._frameRatio ?? r.frameRatio ?? layoutCfg.frameFormat?.ratio) || 16 / 9;

    let frameW = L.desiredFrameW * s;
    let frameH = frameW / frameRatio;

    const maxFrameW = contentW * 0.6;
    if (frameW > maxFrameW) {
      frameW = maxFrameW;
      frameH = frameW / frameRatio;
    }

    const rightW = contentW - frameW - L.gapX;

    const head = headingLine(r, fields);
    const headerPills = [];
    if (fields.sceneNumber) headerPills.push(`SC${r.sceneLabel || pad2(r.sceneIdx || 0)}`);
    if (fields.shotNumber) headerPills.push(r.shotNumber || "—");
    if ((fields.intExt || fields.location || fields.timeOfDay) && head) headerPills.push(head);

    const headerLayout = layoutHeaderPills(fontBold, headerPills, Math.max(10, rightW));
    const headerH = headerLayout.h;

    const tags = tagPills(r, fields, labels, lang);
    const tagsLayout = layoutPills(font, tags, Math.max(10, rightW), 4);
    const tagsH = tagsLayout.h ? 6 + tagsLayout.h : 0;

    const alwaysShowEmpty = layoutCfg.alwaysShowEmpty !== false;

    let desc = null;
    const descHasContent =
      (fields.description && String(r.description || "").trim()) ||
      (fields.lights && String(r.lights || "").trim());
    if ((fields.description || fields.lights) && (alwaysShowEmpty || descHasContent)) {
      const parts = [];
      if (fields.description && r.description) parts.push(r.description);

      if (fields.lights && r.lights) {
        const label = translate(lang, "pdf.field.lights");
        parts.push(`${label}: ${r.lights}`);
      }

      const res = calcTextBoxLinesAndH({
        text: parts.join("\n\n"),
        font,
        w: rightW,
        maxH: L.maxDescH,
        footerLines: 0,
      });

      desc = { ...res, title: boxTitles?.description ?? "Description" };
    }

    let script = null;
    const scriptHasContent = !!String(r.scriptText || "").trim();
    if (fields.scriptText && (alwaysShowEmpty || scriptHasContent)) {
      const res = calcTextBoxLinesAndH({
        text: r.scriptText,
        font: courier,      // wrap en Courier
        w: rightW,
        maxH: L.maxScriptH,
        footerLines: 0,     // ✅ plus de ligne réservée
      });

      script = {
        ...res,
        title: boxTitles?.script ?? "Script",
        headerRightText: r.scriptRef ? `${r.scriptRef}` : "", // ✅ juste "p.X l.Y → ..."
      };
    }

    let imageBox = null;
    if (wantImage && imagePlacement === "underScript" && hasPdfImage) {
      const res = calcImageBoxAndH({
        pdfImage: r._pdfImage,
        w: rightW,
        maxH: L.maxImageH,
      });
      imageBox = { title: boxTitles.image, ...res };
    }

    const rightH =
      headerH +
      (tagsH ? tagsH : 0) +
      (desc ? 8 + desc.h : 0) +
      (script ? 8 + script.h : 0) +
      (imageBox ? 8 + imageBox.h : 0);

    const leftH = frameH + (wantNotes ? L.notesGapFromFrame + notesAreaH(notesLines) : 0);
    const blockH = Math.max(leftH, rightH);

    const cand = {
      s,
      frameW,
      frameH,
      rightW,
      headerPills,
      headerRows: headerLayout.rows,
      tagsRows: tagsLayout.rows,
      desc,
      script,
      imageBox,
      notesLines,
      wantNotes,
      wantImage,
      imagePlacement,
      hasFrameImage: wantImage && imagePlacement === "inFrame" && hasPdfImage,
      blockH,
    };

    if (!best) best = cand;
    if (blockH <= targetH) return cand;
    if (cand.blockH < best.blockH) best = cand;
  }

  return best;
}

function drawSeparator(page, x, y, w) {
  page.drawLine({
    start: { x, y },
    end: { x: x + w, y },
    thickness: L.blockSepThickness,
    color: L.blockSepColor,
  });
}

function drawImageInFrame(page, { x, y, w, h, pdfImage }) {
  const pad = 6;
  const innerW = Math.max(1, w - pad * 2);
  const innerH = Math.max(1, h - pad * 2);

  const iw = pdfImage.width || 1;
  const ih = pdfImage.height || 1;

  let drawW = innerW;
  let drawH = (ih / iw) * drawW;

  if (drawH > innerH) {
    drawH = innerH;
    drawW = (iw / ih) * drawH;
  }

  const dx = x + pad + (innerW - drawW) / 2;
  const dy = y + pad + (innerH - drawH) / 2;

  page.drawImage(pdfImage, { x: dx, y: dy, width: drawW, height: drawH });
}

const SCRIPT_LINES_PER_PAGE = 55; // ajuste si besoin

function upperBound(arr, x) {
  // nb d'éléments <= x
  let lo = 0, hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] <= x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function buildIndexArray(text, ch) {
  const out = [];
  for (let i = 0; i < text.length; i++) if (text[i] === ch) out.push(i);
  return out;
}

function buildScriptLocator(scriptText) {
  const text = scriptText || "";
  const newlines = buildIndexArray(text, "\n");
  const pageBreaks = buildIndexArray(text, "\f"); // form feed = saut de page si présent
  const hasPageBreaks = pageBreaks.length > 0;

  const locate = (idx) => {
    const i = Math.max(0, Math.min(text.length, Number(idx) || 0));

    if (hasPageBreaks) {
      const page = upperBound(pageBreaks, i - 1) + 1;
      const prevBreak = page === 1 ? -1 : pageBreaks[page - 2];
      const pageStart = prevBreak + 1;

      const absNlBefore = upperBound(newlines, i - 1);
      const nlBeforePage = upperBound(newlines, pageStart - 1);
      const lineInPage = (absNlBefore - nlBeforePage) + 1;

      return { page, line: lineInPage };
    }

    // fallback: pages par paquets de lignes
    const absLine = upperBound(newlines, i - 1) + 1; // 1-based
    const page = Math.floor((absLine - 1) / SCRIPT_LINES_PER_PAGE) + 1;
    const line = ((absLine - 1) % SCRIPT_LINES_PER_PAGE) + 1;
    return { page, line };
  };

  const rangeLabel = (startIdx, endIdx) => {
    const s = locate(startIdx);
    // endIdx peut être "fin exclusive", on prend max(end-1, start)
    const eIdx = Math.max(startIdx, (Number(endIdx) || 0) - 1);
    const e = locate(eIdx);

    if (s.page === e.page && s.line === e.line) return `p.${s.page} l.${s.line}`;
    return `p.${s.page} l.${s.line} -> p.${e.page} l.${e.line}`;
  };

  return { locate, rangeLabel };
}


// Petit label "MASTER" (pastille dorée) en bas à droite d'un bloc de plan.
function drawMasterTag(page, font, rightX, y, label = "MASTER") {
  const size = 8.5;
  const padX = 6;
  const padY = 3;
  const w = font.widthOfTextAtSize(label, size) + padX * 2;
  const h = size + padY * 2;
  const x = rightX - w;

  page.drawRectangle({
    x,
    y,
    width: w,
    height: h,
    color: rgb(0.847, 0.663, 0.29), // or doré (#d8a94a)
    borderColor: rgb(0.6, 0.45, 0.15),
    borderWidth: 0.8,
  });
  page.drawText(label, {
    x: x + padX,
    y: y + padY,
    size,
    font,
    color: rgb(0.1, 0.08, 0.02),
  });
}

// Page centrée (couverture / crédits).
function drawSimplePage(pdfDoc, { title, body, fontBold, font }) {
  const page = pdfDoc.addPage([A4.w, A4.h]);
  const margin = 60;
  const maxW = A4.w - margin * 2;

  let y = A4.h * 0.62;

  if (title) {
    const safeTitle = safeWinAnsiText(title);
    // réduit la taille pour tenir dans la largeur, puis ellipse en dernier recours
    let size = 26;
    while (size > 14 && fontBold.widthOfTextAtSize(safeTitle, size) > maxW) size -= 1;
    const drawn = ellipsizeToWidth(fontBold, safeTitle, size, maxW);
    const tw = fontBold.widthOfTextAtSize(drawn, size);
    page.drawText(drawn, {
      x: (A4.w - tw) / 2,
      y,
      size,
      font: fontBold,
      color: rgb(0.06, 0.06, 0.08),
    });
    y -= 40;
  }

  const bodyText = String(body || "").trim();
  if (bodyText) {
    const size = 12;
    const lines = wrapTextToLines(bodyText, font, size, maxW, 60);
    for (const ln of lines) {
      const lw = font.widthOfTextAtSize(ln, size);
      page.drawText(ln, {
        x: (A4.w - Math.min(lw, maxW)) / 2,
        y,
        size,
        font,
        color: rgb(0.15, 0.15, 0.18),
      });
      y -= size + 6;
    }
  }

  return page;
}

export async function buildStoryboardPdf({
  shots,
  scenes,
  scriptText,
  baseName = "projet",
  config,
  lang = "en",
  shotPrefixMode,
  onProgress,
}) {
  const t = (k, vars) => translate(lang, k, vars);
  const cfg = config || {};
  const report = async (p) => {
    if (typeof onProgress === "function") {
      onProgress(p);
      await yieldToUI();
    }
  };

  const fields = {
    sceneNumber: true,
    shotNumber: true,
    intExt: true,
    location: true,
    timeOfDay: true,

    shotSize: true,
    angle: true,
    focus: true,
    framing: true,
    movement: true,
    focal: true,
    lights: true,
    estimation: true,
    ratio: true,
    colorFormat: true,

    description: true,
    scriptText: true,

    image: true,
    notes: true,
    ...(cfg.fields || {}),
  };

  const layoutCfg = {
    imagePlacement: cfg.imagePlacement === "inFrame" ? "inFrame" : "underScript",
    notesLines: clampInt(cfg.notesLines, 1, 6, 2),
    frameFormat: getShotFrameFormat(cfg.frameFormat || "16_9"),
    alwaysShowEmpty: cfg.alwaysShowEmptyFields !== false, // défaut = true
  };

  const cover = cfg.cover && cfg.cover.enabled ? { text: String(cfg.cover.text || "") } : null;
  const credits =
    cfg.credits && cfg.credits.enabled ? { text: String(cfg.credits.text || "") } : null;

  const labels = {
    shotSize: t("pdf.tag.shotSize"),
    angle: t("pdf.tag.angle"),
    focus: t("pdf.tag.focus"),
    framing: t("pdf.tag.framing"),
    movement: t("pdf.tag.movement"),
    focal: t("pdf.tag.focal"),
    lights: t("pdf.tag.lights"),
    estimation: t("pdf.tag.estimation"),
    ratio: t("pdf.tag.ratio"),
    colorFormat: t("pdf.tag.colorFormat"),
  };

  const boxTitles = {
    description: t("pdf.box.description"),
    script: t("pdf.box.script"),
    image: t("pdf.box.image"),
  };

  const { y, m, d } = dateStamp();

  await report(0.04);

  const pdfDoc = await PDFDocument.create();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const courier = await pdfDoc.embedFont(StandardFonts.Courier);

  await report(0.1);

  const sceneIndexById = new Map();
  const sceneLabelById = new Map();
  (scenes || []).forEach((s, idx) => {
    sceneIndexById.set(s.id, idx + 1);
    sceneLabelById.set(s.id, getSceneNumberLabel(s, idx));
  });

  const scriptLocator = buildScriptLocator(scriptText || "");

  const decorated = (shots || []).map((sh) => {
    const sceneIdx = sceneIndexById.get(sh.sceneId) ?? 0;
    const scene = (scenes || []).find((s) => s.id === sh.sceneId) || null;

    const start = safeInt(sh.scriptRange?.start, 0);
    const end = safeInt(sh.scriptRange?.end, 0);
    const a = Math.max(0, Math.min(start, end));
    const b = Math.max(0, Math.max(start, end));
    const scriptRef = (scriptText && b > a)
    ? scriptLocator.rangeLabel(a, b)
    : "";
    const scriptSel = (scriptText || "").slice(a, b).trim();

    const heading = parseSceneHeadingParts(scene?.title || "");
    const { dataUrl: imageDataUrl, type: imageType } = inferImageInfo(sh);

    return {
      // ✅ ordre = ordre d'affichage de la shotlist (listOrder), sinon numérotation, sinon position texte
      sortKey: Number.isFinite(sh.listOrder)
        ? sh.listOrder
        : Number.isFinite(sh.order)
        ? sh.order
        : a,
      sceneIdx,
      sceneLabel: sceneLabelById.get(sh.sceneId) ?? String(sceneIdx),
      isMaster: !!sh.isMaster,
      shotNumber: getShotDisplayNumber(sh.shotNumber, lang, shotPrefixMode),

      intExt: heading.intExt,
      location: heading.location,
      timeOfDay: heading.timeOfDay,

      shotSize: sh.shotSize || "",
      angle: sh.angle || sh.shotType || "",
      focus: sh.focus || "",
      framing: sh.framing || "",
      movement: sh.movement || "",
      focal: sh.lensMm || "",
      estimation: safeInt(sh.durationEstimateSec, 0),
      lights: String(sh.lights ?? "").trim(),

      description: (sh.description || "").trim(),
      scriptStartIndex: a,
      scriptEndIndex: b,
      scriptText: scriptSel,
      scriptRef,

      // format cadre + couleur effectifs (override plan -> scène -> global)
      frameRatio: getShotFrameFormat(effectiveFrameFormatValue(sh, scene, cfg.frameFormat)).ratio,
      ratioLabel: getShotFrameFormat(effectiveFrameFormatValue(sh, scene, cfg.frameFormat)).short,
      colorLabel: translate(lang, getShotColorFormat(effectiveColorFormatValue(sh, scene)).labelKey),

      imageDataUrl,
      imageType,
      _pdfImage: null,
    };
  });

  decorated.sort(
  (a, b) =>
    a.sceneIdx !== b.sceneIdx
      ? a.sceneIdx - b.sceneIdx
      : a.sortKey - b.sortKey
  );

  await report(0.15);

  // ✅ embed images only if enabled
  if (fields.image) {
    const withImages = decorated.filter((r) => r.imageDataUrl);
    let done = 0;
    for (const r of withImages) {
      r._pdfImage = await embedImageIfSupported(pdfDoc, r.imageDataUrl, r.imageType);
      done += 1;
      // 0.15 -> 0.5 pendant l'intégration des images
      await report(0.15 + (withImages.length ? (done / withImages.length) * 0.35 : 0.35));
    }
  }

  await report(0.5);

  const margin = L.margin;
  const contentW = A4.w - margin.l - margin.r;
  const contentH = A4.h - margin.t - margin.b - L.headerH;

  const blocks = decorated.map((r) => ({
    r,
    metrics: computeBlock(font, fontBold, courier, r, fields, layoutCfg, contentW, contentH, labels, lang, boxTitles),
  }));

  // pack blocks into pages
  const pages = [];
  let current = [];
  let remaining = contentH;

  for (const b of blocks) {
    const need = b.metrics.blockH + (current.length ? L.gapBlocks : 0);
    if (current.length && need > remaining) {
      pages.push(current);
      current = [b];
      remaining = contentH - b.metrics.blockH;
    } else {
      current.push(b);
      remaining -= need;
    }
  }
  if (current.length) pages.push(current);

  const totalPages = Math.max(1, pages.length);

  await report(0.65);

  // ✅ Page de couverture (Bug 11)
  if (cover) {
    drawSimplePage(pdfDoc, {
      title: baseName,
      body: cover.text,
      fontBold,
      font,
    });
  }

  for (let p = 0; p < pages.length; p++) {
    // 0.65 -> 0.92 pendant le rendu des pages
    await report(0.65 + ((p + 1) / totalPages) * 0.27);
    const page = pdfDoc.addPage([A4.w, A4.h]);

    const topY = A4.h - margin.t;
    const headerSize = 11;

    // ✅ le libellé de page occupe la droite : on réduit le titre projet pour ne pas déborder
    const pageLabel = `${t("pdf.header.pages")} ${pad2(p + 1)}/${pad2(totalPages)}`;
    const pageLabelW = font.widthOfTextAtSize(pageLabel, headerSize);

    const projectFull = `${t("pdf.header.project")} ${baseName}`;
    const projectMaxW = Math.max(40, contentW - pageLabelW - 16);
    const projectText = ellipsizeToWidth(fontBold, projectFull, headerSize, projectMaxW);

    page.drawText(projectText, {
      x: margin.l,
      y: topY - headerSize,
      size: headerSize,
      font: fontBold,
      color: rgb(0, 0, 0),
    });

    page.drawText(pageLabel, {
      x: margin.l + contentW - pageLabelW,
      y: topY - headerSize,
      size: headerSize,
      font,
      color: rgb(0, 0, 0),
    });

    let cursorTop = topY - L.headerH;

    for (let i = 0; i < pages[p].length; i++) {
      const { r, metrics } = pages[p][i];

      const blockTopY = cursorTop;
      const blockH = metrics.blockH;
      const blockBottomY = blockTopY - blockH;

      // Left: frame
      const frameX = margin.l;
      const frameTopY = blockTopY;
      const frameY = frameTopY - metrics.frameH;

      drawBox(page, frameX, frameY, metrics.frameW, metrics.frameH, 1);

      // ✅ Image in main frame (optional)
      if (metrics.hasFrameImage && r._pdfImage) {
        drawImageInFrame(page, {
          x: frameX,
          y: frameY,
          w: metrics.frameW,
          h: metrics.frameH,
          pdfImage: r._pdfImage,
        });
      }

      // ✅ Notes lines (no box)
      if (fields.notes) {
        const notesTopY = frameY - L.notesGapFromFrame;
        drawNotesArea(page, {
          x: frameX,
          topY: notesTopY,
          w: metrics.frameW,
          linesCount: metrics.notesLines,
        });
      }

      // Right column
      const rightX = frameX + metrics.frameW + L.gapX;
      const rightW = metrics.rightW;

      let yRight = blockTopY;

      // Header pills multi-lignes (SC / Shot / EXT - LOC - DAY)
      if (metrics.headerRows && metrics.headerRows.length) {
        const drawnHeader = drawHeaderPills(page, fontBold, rightX, yRight - 2, {
          rows: metrics.headerRows,
        });

        yRight = drawnHeader.bottomY - 8;
      }

      // Tags pills
      if (metrics.tagsRows && metrics.tagsRows.length) {
        const drawn = drawPillRows(page, font, rightX, yRight, metrics.tagsRows);
        yRight = drawn.bottomY - 10;
      }

      // Description
      if (metrics.desc) {
        drawTextBox(page, {
          x: rightX,
          topY: yRight,
          w: rightW,
          title: metrics.desc.title,
          lines: metrics.desc.lines,
          h: metrics.desc.h,
          font,
          fontBold,
        });
        yRight -= metrics.desc.h + 8;
      }

      // Script
      if (metrics.script) {
        drawTextBox(page, {
          x: rightX,
          topY: yRight,
          w: rightW,
          title: metrics.script.title,
          lines: metrics.script.lines,
          h: metrics.script.h,
          font,
          fontBold,
          bodyFont: courier, // ✅ Script en Courier

          headerRightText: metrics.script.headerRightText || "",
          headerRightFont: courier,     // repère aussi en Courier (cohérent script)
          headerRightSize: 8.2,
        });

        yRight -= metrics.script.h + 8;
      }


      // ✅ Image under script
      if (metrics.imageBox && r._pdfImage) {
        drawImageBox(page, {
          x: rightX,
          topY: yRight,
          w: rightW,
          title: metrics.imageBox.title,
          h: metrics.imageBox.h,
          drawW: metrics.imageBox.drawW,
          drawH: metrics.imageBox.drawH,
          fontBold,
          pdfImage: r._pdfImage,
        });
        yRight -= metrics.imageBox.h + 8;
      }

      // ✅ separator between blocks
      const isLastOnPage = i === pages[p].length - 1;
      if (!isLastOnPage) {
        drawSeparator(page, margin.l, blockBottomY - L.gapBlocks / 2, contentW);
      }

      cursorTop = blockBottomY - L.gapBlocks;
    }
  }

  // ✅ Page de crédits (Bug 11)
  if (credits) {
    drawSimplePage(pdfDoc, {
      title: t("pdf.credits.pageTitle"),
      body: credits.text,
      fontBold,
      font,
    });
  }

  await report(0.94);

  const bytes = await pdfDoc.save();
  const filename = `${baseName}_storyboard_${y}-${m}-${d}.pdf`;
  const blob = new Blob([bytes], { type: "application/pdf" });

  await report(1);

  return { filename, blob };
}
